import React, { useState } from 'react';
import { tw } from 'twind';
import { Button, ButtonCta } from '@global-ecom/nitro-uds/elements';

import { useTranslate } from 'hooks/useTranslations';
import { Link } from 'ui/elements/Link';
import CloseButton from 'ui/components/CloseButton';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { cookieOptions, useCookieSettings } from 'hooks/useCookieSettings';
import { ToggleSwitch } from 'ui/elements/ToggleSwitch';
import { SUPPORTED_COUNTRY } from 'utils/constants';

const CookieBanner = () => {
  const t = useTranslate();
  const {
    form,
    showCookieBanner,
    showCookieSettings,
    setShowCookieSettings,
    acceptCookies,
  } = useCookieSettings();
  const [cookieState, setCookieState] = useState(cookieOptions[0]);
  const { country } = useSiteConfig();
  if (!showCookieBanner && !showCookieSettings) {
    return null;
  }
  return (
    <div
      className={`fixed bottom-0 w-full flex items-center justify-center z-[90]`}
      role="dialog"
      aria-modal="false"
      data-test-id="cookie-banner"
    >
      <CloseButton
        className={`${
          country === SUPPORTED_COUNTRY.US || SUPPORTED_COUNTRY.CA
            ? 'hidden'
            : 'absolute top-0 right-0'
        } `}
        onClick={() => acceptCookies()}
        invert
        size="sm"
      />
      {showCookieSettings ? (
        <div className="w-full h-[100dvh] flex flex-col justify-end bg-puma-black bg-opacity-50">
          <div className="bg-white w-full h-full border overflow-y-scroll overflow-hidden lg:h-60 xl:h-52 pb-0">
            <div className="desktop:flex desktop:items-start">
              <div className="mobile:flex mobile:justify-end mobile:pr-5 tablet:flex tablet:justify-end tablet:pr-5 hidden">
                <CloseButton
                  className={`pt-6 px-7 ${
                    country === SUPPORTED_COUNTRY.US || SUPPORTED_COUNTRY.CA
                      ? 'desktop:hidden flex justify-self-end'
                      : 'hidden'
                  } `}
                  onClick={() => setShowCookieSettings(false)}
                  size="base"
                />
              </div>
              {cookieOptions.map(cookie => (
                <div
                  key={cookie.label}
                  className={tw(
                    'pt-6 px-7 mx-auto animate-fade-in max-w-screen-3xl lg:pr-24 lg:pl-6 3xl:pl-0',
                    cookieState.label !== cookie.label && 'lg:hidden'
                  )}
                >
                  <div className="flex justify-between">
                    <p className="font-bold text-xl uppercase border-l-4 border-[#A3885f] pl-1 lg:border-none">
                      {t(cookie.title as any)}
                    </p>
                    {cookie.toggle ? (
                      <ToggleSwitch
                        ref={form.register()}
                        name={cookie.label}
                        id={cookie.title}
                      />
                    ) : (
                      <span className="uppercase text-puma-gold">
                        {t('alwaysActive')}
                      </span>
                    )}
                  </div>
                  <p className="py-4">{t(cookie.label as any)}</p>
                </div>
              ))}
              <CloseButton
                className={`pt-5 pr-7 ${
                  country === SUPPORTED_COUNTRY.US || SUPPORTED_COUNTRY.CA
                    ? 'mobile:hidden tablet:hidden'
                    : 'hidden'
                } `}
                onClick={() => setShowCookieSettings(false)}
                size="base"
              />
            </div>
            <div className="px-8 p-8 lg:hidden">
              <Button
                label={t('confirmMyChoice')}
                onClick={acceptCookies}
                variant="primary"
                className="w-full"
              />
            </div>
          </div>
          <div className="bg-puma-black-100 p-8 w-full flex hidden lg:flex">
            <div className="flex max-w-screen-3xl w-full m-auto">
              <div className="flex flex-1 items-center">
                <ul className="flex text-white" role="menubar">
                  {cookieOptions.map(selection => (
                    <li
                      key={selection.label}
                      className="cursor-pointer pr-10 xl:pr-12"
                      onClick={() => setCookieState(selection)}
                    >
                      <span
                        className={tw(
                          'text-base block mt-1 pb-1 whitespace-nowrap nav-underline font-bold 3xl:text-lg uppercase hover:nav-underline-selected',
                          cookieState?.label === selection.label &&
                            'nav-underline-selected'
                        )}
                      >
                        {t(selection.title as any)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex lg:justify-end">
                <div className="flex justify-end w-full">
                  <Button
                    label={t('confirmMyChoice')}
                    onClick={acceptCookies}
                    variant="primary"
                    className="whitespace-nowrap px-4 w-full px-2 hover:bg-[#A3885F] xl:px-8 xl:mx-4"
                  />
                  <Button
                    label={t('acceptAll')}
                    onClick={() => {
                      form.setValue('userExperience', true);
                      form.setValue('marketing', true);
                      acceptCookies();
                    }}
                    variant="primary"
                    className="whitespace-nowrap w-full xs:w-auto px-8 hidden hover:bg-[#A3885F] xl:flex text-sm xl:text-base"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : country === SUPPORTED_COUNTRY.US || SUPPORTED_COUNTRY.CA ? (
        <div className="bg-puma-black-100 py-5 mobile:px-4 px-8 w-full">
          <div className="flex w-full justify-between items-center">
            <p className="text-white" data-test-id="cookie-banner-message">
              <Link
                href="/help/privacy-policy"
                rel="noreferrer"
                className="text-white text-sm font-bold hover:text-[#DFE0E1]"
              >
                {t('cookieNoticeCTAText')}
              </Link>
              <span> {t('or')} </span>
              <span
                className="text-white cursor-pointer font-bold text-sm hover:text-[#DFE0E1]"
                onClick={() => setShowCookieSettings(true)}
              >
                <span className="mobile:inline tablet:inline desktop:hidden">
                  {t('tapHere')}
                </span>
                <span className="mobile:hidden tablet:hidden desktop:inline">
                  {t('clickHere')}
                </span>
              </span>{' '}
              <span>{t('optOutCookies')}</span>
            </p>
            <div className="flex justify-between align-center">
              <ButtonCta
                invert
                label={t('manageCookies')}
                mode="text"
                size="base"
                variant="primary"
                className="mobile:hidden tablet:hidden desktop:inline"
                onClick={() => setShowCookieSettings(true)}
              />
              <ButtonCta
                className="ml-3 mobile:hidden tablet:hidden desktop:inline"
                invert
                label={t('allow')}
                mode="text"
                size="base"
                variant="secondary"
                onClick={() => {
                  form.setValue('userExperience', true);
                  form.setValue('marketing', true);
                  acceptCookies();
                }}
              />
              <CloseButton
                className="mobile:ml-4 tablet:ml-6 ml-14"
                dataTestId="cookie-banner-close-btn"
                onClick={() => acceptCookies()}
                invert
                size="sm"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-puma-black-100 py-6 px-4 md:px-24 w-full">
          <div className="flex w-full flex-col space-y-6">
            <div>
              <p className="text-white text-lg mb-2 font-bold leading-none uppercase">
                {t('cookieNoticeHeader')}
              </p>
              <p className="text-white" data-test-id="cookie-banner-message">
                <span className="hidden md:inline">
                  {t('cookieNoticeText')}&nbsp;
                </span>
                <Link
                  href="/help/privacy-policy"
                  rel="noreferrer"
                  className="text-white underline text-sm font-bold hover:text-[#DFE0E1]"
                >
                  {t('cookieNoticeCTAText')}
                </Link>
                <span className="md:hidden"> {t('or')} </span>
                <span className="hidden md:inline">{` ${t('orClick')} `}</span>
                <span
                  className="text-white underline cursor-pointer font-bold text-sm hover:text-[#DFE0E1]"
                  onClick={() => setShowCookieSettings(true)}
                >
                  <span className="md:hidden">{t('clickHere')}</span>
                  <span className="hidden sm:inline">{t('manageCookies')}</span>
                </span>{' '}
                <span className="hidden md:inline">{t('optOutCookies')}</span>
                <span className="md:hidden">{` ${t('cookiesMobile')}`}</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieBanner;
